// src/editor/context/EditorContext/EditorContext.jsx
import { customAlphabet } from 'nanoid/non-secure';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
    finalizePagePath,
    sanitizePagePathInput,
} from '../../helpers/pagePath';
import { API } from '../../utils/api';
import { useNotifications } from '../NotificationContext/NotificationContext';

// ✅ preload local globals.json for consistent logo and global data on reload
import company_data from '../../../../public/static/json/globals.json'; // adjust path if needed

const RBG_HEX_REGEX = /^#[0-9A-F]{6}$/i;
export const VARIANTS = ['primary', 'secondary'];
export const SHADES = [
    '50',
    '100',
    '200',
    '300',
    '400',
    '500',
    '600',
    '700',
    '800',
    '900',
    '950',
];
export const FONT_FAMILIES = ['display', 'text'];
export const BUTTON_VARIANTS = ['primary', 'secondary'];
export const BUTTON_STATES = ['default', 'hover'];
const BUTTON_HOVER_ANIMATIONS = new Set([
    'none',
    'glow',
    'gradual-fill',
    'slide-up',
    'slide-right',
    'shimmer',
    'rotating-border',
    'magnet',
    'underline-draw',
    'gradient-shift',
    'pulse-ring',
    'tilt',
    'icon-slide',
    'icon-nudge',
    'icon-fill-slide',
    'icon-show',
    'icon-reveal',
]);
const BUTTON_ICON_SIZES = new Set(['small', 'medium', 'large', 'x-large']);

const HEX6_RE = /^#([0-9a-fA-F]{6})$/;
const HEX3_RE = /^#([0-9a-fA-F]{3})$/;
const HEX8_RE = /^#([0-9a-fA-F]{8})$/;

const DEFAULT_BUTTON_SIZING_PRESET_KEY = 'regular';
const BUTTON_SIZING_PRESETS = [
    {
        key: 'x-small',
        label: 'X-Small',
        sm: {
            px: '0.6875rem',
            py: '0.4375rem',
            fontSize: '0.75rem',
            lineHeight: '1rem',
        },
        md: {
            px: '0.8125rem',
            py: '0.5625rem',
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
        },
        lg: {
            px: '0.9375rem',
            py: '0.6875rem',
            fontSize: '1rem',
            lineHeight: '1.5rem',
        },
        xl: {
            px: '1.125rem',
            py: '0.875rem',
            fontSize: '1.125rem',
            lineHeight: '1.5rem',
        },
    },
    {
        key: 'small',
        label: 'Small',
        sm: {
            px: '0.875rem',
            py: '0.625rem',
            fontSize: '0.8125rem',
            lineHeight: '1.125rem',
        },
        md: {
            px: '1rem',
            py: '0.75rem',
            fontSize: '0.9375rem',
            lineHeight: '1.25rem',
        },
        lg: {
            px: '1.125rem',
            py: '0.875rem',
            fontSize: '1.0625rem',
            lineHeight: '1.5rem',
        },
        xl: {
            px: '1.375rem',
            py: '1rem',
            fontSize: '1.25rem',
            lineHeight: '1.75rem',
        },
    },
    {
        key: 'regular',
        label: 'Regular',
        sm: {
            px: '1rem',
            py: '0.75rem',
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
        },
        md: {
            px: '1.25rem',
            py: '1rem',
            fontSize: '1.125rem',
            lineHeight: '1.75rem',
        },
        lg: {
            px: '1.5rem',
            py: '1.25rem',
            fontSize: '1.25rem',
            lineHeight: '1.75rem',
        },
        xl: {
            px: '1.75rem',
            py: '1.5rem',
            fontSize: '1.5rem',
            lineHeight: '2rem',
        },
    },
    {
        key: 'large',
        label: 'Large',
        sm: {
            px: '1.125rem',
            py: '0.875rem',
            fontSize: '0.9375rem',
            lineHeight: '1.375rem',
        },
        md: {
            px: '1.5rem',
            py: '1.125rem',
            fontSize: '1.25rem',
            lineHeight: '1.75rem',
        },
        lg: {
            px: '1.75rem',
            py: '1.375rem',
            fontSize: '1.5rem',
            lineHeight: '2rem',
        },
        xl: {
            px: '2rem',
            py: '1.625rem',
            fontSize: '1.75rem',
            lineHeight: '2.25rem',
        },
    },
    {
        key: 'x-large',
        label: 'X-Large',
        sm: {
            px: '1.25rem',
            py: '1rem',
            fontSize: '1.0625rem',
            lineHeight: '1.5rem',
        },
        md: {
            px: '1.75rem',
            py: '1.25rem',
            fontSize: '1.5rem',
            lineHeight: '2rem',
        },
        lg: {
            px: '2rem',
            py: '1.5rem',
            fontSize: '1.75rem',
            lineHeight: '2.25rem',
        },
        xl: {
            px: '2.25rem',
            py: '1.75rem',
            fontSize: '2rem',
            lineHeight: '2.5rem',
        },
    },
];
const BUTTON_SIZING_PRESET_MAP = new Map(
    BUTTON_SIZING_PRESETS.map((preset) => [preset.key, preset])
);

const nanoid = customAlphabet('1234567890abcdefghijklmnopqrstuvwxyz', 4);
const PAGE_PUBLISHED_KEY = 'Published';
const PAGE_UNPUBLISH_REDIRECT_KEY = 'UnpublishRedirectTo';

const EditorContext = createContext({
    global: { data: {}, change: () => {}, cancel: () => {} },
    site: {
        data: {},
        change: () => {},
        cancel: () => {},
        addImage: () => {},
        deleteImage: () => {},
        regenerating_palette: () => false,
        visible: () => false,
        open: () => {},
        close: () => {},
    },
    page: {
        name: '',
        path: '',
        data: {},
        changePath: () => {},
        change: () => {},
        cancel: () => {},
        add: () => {},
        adding: () => false,
        delete: () => {},
        deleting: () => false,
        visible: () => false,
        open: () => {},
        close: () => {},
    },
    components: {
        data: {},
        change: () => {},
        cancel: () => {},
        generate: () => {},
        generating: () => false,
        add: () => {},
        duplicate: () => {},
        move: () => {},
        delete: () => {},
        visible: () => false,
        open: () => {},
        close: () => {},
        add_panel: {
            open: () => {},
            close: () => {},
            visible: () => false,
            index: () => null,
        },
        nested: { add: () => {}, delete: () => {}, move: () => {} },
    },
    is_saving: false,
    has_updates: false,
    save: () => {},
    revert: () => {},
});

export function useEditor() {
    return useContext(EditorContext);
}

/* ---------------- contrast helpers (hex -> best text color) ---------------- */
const hexToRgb = (hex) => {
    const m = String(hex).trim().replace('#', '');
    const n =
        m.length === 3
            ? m
                  .split('')
                  .map((c) => c + c)
                  .join('')
            : m.length === 8
            ? m.slice(0, 6)
            : m;
    const int = parseInt(n, 16);
    return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
};
const relLuminance = ({ r, g, b }) => {
    const toLin = (v) => {
        const s = v / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    };
    const R = toLin(r),
        G = toLin(g),
        B = toLin(b);
    return 0.2126 * R + 0.7152 * G + 0.0722 * B;
};
// Returns 'black' or 'white' for best readability
const pickTextColor = (hex) => {
    try {
        const L = relLuminance(hexToRgb(hex));
        return L > 0.5 ? 'black' : 'white';
    } catch {
        return 'white';
    }
};

const normalizeHexColor = (input) => {
    if (!input) return null;
    let v = String(input).trim();
    if (!v) return null;
    if (v === 'white') return '#FFFFFF';
    if (v === 'black') return '#000000';
    if (/^var\(--/.test(v) || v === 'transparent' || v === 'currentColor') {
        return v;
    }
    if (!v.startsWith('#')) v = `#${v}`;
    if (HEX3_RE.test(v)) {
        v = v.replace(HEX3_RE, (_, s) => {
            const [r, g, b] = s.split('');
            return `#${r}${r}${g}${g}${b}${b}`;
        });
    }
    return HEX6_RE.test(v) || HEX8_RE.test(v) ? v.toUpperCase() : null;
};

const TRANSPARENT_BORDER_GRADIENT = 'linear-gradient(transparent, transparent)';
const DEFAULT_PRIMARY_BORDER_DEFAULT_GRADIENT =
    'linear-gradient(var(--primary-700), var(--primary-700))';
const DEFAULT_PRIMARY_BORDER_HOVER_GRADIENT =
    'linear-gradient(var(--primary-600), var(--primary-600))';
const DEFAULT_SECONDARY_BORDER_DEFAULT_GRADIENT =
    'linear-gradient(var(--secondary-700), var(--secondary-700))';
const DEFAULT_SECONDARY_BORDER_HOVER_GRADIENT =
    'linear-gradient(var(--secondary-600), var(--secondary-600))';

const normalizeGradientToken = (input) =>
    String(input ?? '')
        .replace(/\s+/g, '')
        .toUpperCase();

const isLegacyPrimaryBorderDefault = (input) => {
    const normalized = normalizeGradientToken(input);
    return (
        normalized === '#1B7CE4' ||
        normalized === 'LINEAR-GRADIENT(#1B7CE4,#1B7CE4)'
    );
};

const isLegacySecondaryBorderDefault = (input) => {
    const normalized = normalizeGradientToken(input);
    return (
        normalized === '#1AB261' ||
        normalized === 'LINEAR-GRADIENT(#1AB261,#1AB261)'
    );
};

const normalizeBorderImageSource = (input) => {
    const trimmed = String(input ?? '').trim();
    if (!trimmed || trimmed === 'none') {
        return TRANSPARENT_BORDER_GRADIENT;
    }
    if (/^linear-gradient\(/i.test(trimmed)) {
        return trimmed;
    }
    return `linear-gradient(${trimmed}, ${trimmed})`;
};

const normalizeButtonFillSource = (input) => {
    const trimmed = String(input ?? '').trim();
    if (!trimmed || trimmed === 'none') {
        return 'linear-gradient(transparent, transparent)';
    }
    if (/^linear-gradient\(/i.test(trimmed)) {
        return trimmed;
    }
    return `linear-gradient(${trimmed}, ${trimmed})`;
};

const getButtonFillSolidToken = (input, fallback = 'currentColor') => {
    const tokens = extractGradientColorTokens(input);
    const token = tokens.find(
        (entry) =>
            entry &&
            String(entry).trim() &&
            String(entry).trim().toLowerCase() !== 'transparent'
    );
    return token || fallback;
};

const extractGradientColorTokens = (input) => {
    const trimmed = String(input ?? '').trim();
    if (!trimmed || trimmed === 'none') return [];
    if (!/^linear-gradient\(/i.test(trimmed)) return [trimmed];

    const match = trimmed.match(/^linear-gradient\((.*)\)$/i);
    if (!match) return [];
    const parts = match[1]
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean);
    if (!parts.length) return [];

    const start = /^-?\d+(?:\.\d+)?deg$/i.test(parts[0]) ? 1 : 0;
    return parts.slice(start).map((entry) => entry.split(/\s+/)[0]);
};

const resolveColorTokenToHex = (token, getVar, palette = {}) => {
    const normalized = normalizeHexColor(token);
    if (!normalized) return null;
    if (normalized === 'white') return '#FFFFFF';
    if (normalized === 'black') return '#000000';
    if (normalized.startsWith('#')) return normalized.toUpperCase();

    const varMatch = normalized.match(/^var\(--([^)]+)\)$/i);
    if (varMatch && typeof getVar === 'function') {
        const paletteValue = normalizeHexColor(palette?.[varMatch[1]]);
        if (paletteValue?.startsWith('#')) return paletteValue.toUpperCase();

        const resolved = String(getVar(`--${varMatch[1]}`) || '').trim();
        if (!resolved) return null;
        const candidates = extractGradientColorTokens(resolved);
        for (const candidate of candidates) {
            const hex = resolveColorTokenToHex(candidate, getVar, palette);
            if (hex) return hex;
        }
    }

    return null;
};

const getHexAlpha = (hex) => {
    const normalized = normalizeHexColor(hex);
    if (!normalized || !normalized.startsWith('#')) return 1;
    if (normalized.length !== 9) return 1;
    const parsed = Number.parseInt(normalized.slice(7), 16);
    if (!Number.isFinite(parsed)) return 1;
    return Math.max(0, Math.min(1, parsed / 255));
};

const buildButtonShadowValue = (
    fillSource,
    getVar,
    palette = {},
    fallbackSource = ''
) => {
    const tokens = [
        ...extractGradientColorTokens(fillSource),
        ...extractGradientColorTokens(fallbackSource),
    ];
    for (const token of tokens) {
        const hex = resolveColorTokenToHex(token, getVar, palette);
        if (!hex) continue;
        try {
            const { r, g, b } = hexToRgb(hex);
            const alpha = getHexAlpha(hex);
            const shadowAlpha = 0.35 * alpha;
            if (shadowAlpha < 0.02) continue;
            return `0 20px 44px -12px rgba(${r}, ${g}, ${b}, ${shadowAlpha.toFixed(
                3
            )})`;
        } catch {
            continue;
        }
    }
    return 'none';
};

const isShadowEnabledValue = (value) => {
    const normalized = String(value || '')
        .trim()
        .toLowerCase();
    return !!normalized && normalized !== 'none';
};

const normalizeButtonHoverAnimationValue = (value) => {
    const normalized = String(value || '')
        .trim()
        .toLowerCase();
    if (normalized === 'slide') return 'slide-up';
    if (normalized === 'gradualfill') return 'gradual-fill';
    if (normalized === 'rotatinggradientborder') return 'rotating-border';
    if (normalized === 'rotatingborder') return 'rotating-border';
    if (normalized === 'underlinedraw') return 'underline-draw';
    if (normalized === 'gradientshift') return 'gradient-shift';
    if (normalized === 'pulsering') return 'pulse-ring';
    if (normalized === 'iconslide') return 'icon-slide';
    if (normalized === 'iconnudge') return 'icon-nudge';
    if (normalized === 'iconfillslide') return 'icon-fill-slide';
    if (normalized === 'iconcapsulesweep') return 'icon-show';
    if (normalized === 'iconhighlight') return 'icon-show';
    if (normalized === 'iconshow') return 'icon-show';
    if (normalized === 'iconreveal') return 'icon-reveal';
    return BUTTON_HOVER_ANIMATIONS.has(normalized) ? normalized : 'none';
};

const normalizeButtonIconClassValue = (value, fallback = 'none') => {
    const normalized = String(value ?? '')
        .trim()
        .replace(/^['"]|['"]$/g, '')
        .replace(/\s+/g, ' ');
    if (!normalized || normalized.toLowerCase() === 'none') {
        return fallback;
    }
    return normalized;
};

const normalizeButtonIconSizeValue = (value, fallback = 'medium') => {
    const normalized = String(value ?? '')
        .trim()
        .toLowerCase();
    if (
        normalized === 'xl' ||
        normalized === 'xlarge' ||
        normalized === 'extra-large' ||
        normalized === 'extralarge'
    ) {
        return 'x-large';
    }
    if (BUTTON_ICON_SIZES.has(normalized)) return normalized;
    return fallback;
};

const parseSolidGradientColorToken = (input) => {
    const trimmed = String(input ?? '').trim();
    if (!trimmed || trimmed === 'none') return null;

    if (/^linear-gradient\(/i.test(trimmed)) {
        const match = trimmed.match(/^linear-gradient\((.*)\)$/i);
        if (!match) return null;
        const parts = match[1]
            .split(',')
            .map((entry) => entry.trim())
            .filter(Boolean);
        if (parts.length < 2) return null;

        let start = 0;
        if (/^-?\d+(?:\.\d+)?deg$/i.test(parts[0])) {
            start = 1;
        }

        const stops = parts
            .slice(start)
            .map((entry) => entry.split(/\s+/)[0])
            .map((entry) => normalizeHexColor(entry))
            .filter(Boolean);
        if (!stops.length) return null;

        const first = stops[0];
        if (stops.every((entry) => entry === first)) return first;
        return null;
    }

    return normalizeHexColor(trimmed);
};

const inferThemeShadeFromSource = (variant, source, palette = {}) => {
    const token = parseSolidGradientColorToken(source);
    if (!token || token === 'transparent' || token === 'currentColor') {
        return null;
    }

    const varMatch = String(token).match(
        /^var\(--(primary|secondary)-(\d{2,3})\)$/i
    );
    if (varMatch) {
        return varMatch[1].toLowerCase() === variant
            ? String(varMatch[2])
            : null;
    }

    const hex = normalizeHexColor(token);
    if (!hex || !hex.startsWith('#')) return null;

    return (
        SHADES.find(
            (shade) =>
                String(palette?.[`${variant}-${shade}`] || '').toUpperCase() ===
                hex.toUpperCase()
        ) || null
    );
};

// Update CSS variables on both the main document and the preview iframe
const getPreviewDocumentRoot = () => {
    if (typeof document === 'undefined') return null;
    const iframe = document.querySelector('iframe[title="Page Preview"]');
    return iframe?.contentDocument?.documentElement || null;
};

const withDocumentRoots = (cb) => {
    if (typeof document === 'undefined') return;
    const previewRoot = getPreviewDocumentRoot();
    const roots = [document.documentElement];
    if (previewRoot) roots.push(previewRoot);
    roots.forEach((root) => cb(root));
};

const setCssVar = (name, value) =>
    withDocumentRoots((root) => root.style.setProperty(name, value));

const readCssVar = (name) => {
    if (typeof document === 'undefined') return '';
    const previewRoot = getPreviewDocumentRoot();
    const roots = [previewRoot, document.documentElement].filter(Boolean);
    for (const root of roots) {
        const val =
            root.style.getPropertyValue(name) ||
            getComputedStyle(root).getPropertyValue(name);
        if (val?.trim()) return val.trim();
    }
    return '';
};

async function fetchCompanyImages(companyId) {
    const response = await fetch(
        `https://image-search-ytnjkd5upa-uc.a.run.app/images/${companyId}?n=500`
    );
    if (!response.ok) {
        throw new Error(`Failed to load images: ${response.status}`);
    }
    return response.json();
}

function parsePublishedValue(value) {
    if (value == null) return true;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['false', '0', 'no', 'off'].includes(normalized)) return false;
        if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    }
    return Boolean(value);
}

function normalizeRoutePath(value) {
    const raw = String(value ?? '').trim();
    if (!raw || raw === 'index' || raw === '/') return '/';
    const cleaned = raw.replace(/^\/+|\/+$/g, '');
    return cleaned ? `/${cleaned}` : '/';
}

function normalizeUnpublishRedirectTarget(value) {
    const raw = String(value ?? '').trim();
    if (!raw) return '';

    try {
        const parsed = /^https?:\/\//i.test(raw)
            ? new URL(raw)
            : new URL(raw.startsWith('/') ? raw : `/${raw}`, 'https://local');
        const pathname = (parsed.pathname || '/').replace(/\/+$/, '') || '/';
        return `${pathname}${parsed.search || ''}${parsed.hash || ''}`;
    } catch (error) {
        return '';
    }
}

function getTargetPathname(target) {
    const normalized = normalizeUnpublishRedirectTarget(target);
    if (!normalized) return '';

    try {
        const parsed = new URL(normalized, 'https://local');
        return (parsed.pathname || '/').replace(/\/+$/, '') || '/';
    } catch (error) {
        return '';
    }
}

export function EditorContextProvider(props) {
    const {
        children,
        page_name,
        global_data: input_global_data,
        page_data: input_page_data,
        components: input_components,
    } = props;

    const notifications = useNotifications();

    /* ------------------------------------------------------------------------
       ✅ Global data initialization fix:
       Use input_global_data if provided, otherwise preload from globals.json.
       This ensures logoUrl and company info are always available on reload.
    ------------------------------------------------------------------------ */
    const initialGlobalData =
        input_global_data && Object.keys(input_global_data).length
            ? input_global_data
            : company_data;

    // ----------------- global.json state -----------------
    const [saved_global_data, setSavedGlobalData] = useState(initialGlobalData);
    const [global_data, setGlobalData] = useState(initialGlobalData);

    // ----------------- site settings state -----------------
    const [is_site_settings_open, setIsSiteSettingsOpen] = useState(false);
    const [saved_site_data, setSavedSiteData] = useState({
        colors: {},
        fonts: {},
        fontWeights: {},
        shape: {},
        buttonSizing: {},
        images: [],
    });
    const [site_data, setSiteData] = useState({
        colors: {},
        fonts: {},
        fontWeights: {},
        shape: {},
        buttonSizing: {},
        images: [],
    });
    const [regenerating_palette, setRegeneratingPalette] = useState({});

    // ----------------- page state -----------------
    const [page_settings_open_mode, setIsPageSettingsOpen] = useState(null);
    const initial_page_path = page_name === 'index' ? '' : page_name;
    const [saved_page_path, setSavedPagePath] = useState(initial_page_path);
    const [page_path, setPagePath] = useState(initial_page_path);
    const [saved_page_data, setSavedPageData] = useState(input_page_data);
    const [page_data, setPageData] = useState(input_page_data);
    const [adding_page, setAddingPage] = useState(false);
    const [deleting_page, setDeletingPage] = useState(false);

    // ----------------- components state -----------------
    const [saved_components, setSavedComponents] = useState(input_components);
    const [components, setComponents] = useState(input_components);

    const [component_editor_open_index, setComponentEditorOpenIndex] =
        useState(null);
    const [add_component_open_index, setAddComponentOpenIndex] = useState(null);
    const [component_generating, setComponentGenerating] = useState({});

    // ----------------- save state -----------------
    const [is_saving, setIsSaving] = useState(false);

    // Derived flag (no setState loop)
    const has_updates = useMemo(() => {
        const diff = (a, b) => JSON.stringify(a) !== JSON.stringify(b);
        return (
            diff(components, saved_components) ||
            diff(page_path, saved_page_path) ||
            diff(page_data, saved_page_data) ||
            diff(site_data, saved_site_data) ||
            diff(global_data, saved_global_data)
        );
    }, [
        components,
        saved_components,
        page_path,
        saved_page_path,
        page_data,
        saved_page_data,
        site_data,
        saved_site_data,
        global_data,
        saved_global_data,
    ]);

    //==========================================================================//
    //============================= GLOBAL CONTEXT =============================//
    //==========================================================================//

    const handleGlobalDataChange = ({ key, value }) => {
        setGlobalData((prev) => ({ ...prev, [key]: value }));
    };

    const handleUndoGlobalDataChanges = () => {
        setGlobalData(saved_global_data);
    };

    // ✅ Ensure that if context ever loses logoUrl, we rehydrate from globals.json
    useEffect(() => {
        if (!global_data.logoUrl && company_data.logoUrl) {
            setGlobalData((prev) => ({
                ...prev,
                logoUrl: company_data.logoUrl,
            }));
        }
    }, [global_data]);

    //==========================================================================//
    //============================== SITE CONTEXT ==============================//
    //==========================================================================//

    // Load fonts/colors/shape from CSS variables on mount
    useEffect(() => {
        const root = document.documentElement;
        const saved_fonts = {};
        const saved_fontWeights = {};
        const saved_colors = { palette: {}, button: {} };

        const saved_shape_radius =
            getComputedStyle(root).getPropertyValue(`--shape-radius`);

        FONT_FAMILIES.forEach((font_family) => {
            saved_fonts[font_family] = getComputedStyle(root)
                .getPropertyValue(`--ff-${font_family}`)
                .replace(/"/g, '');
            saved_fontWeights[font_family] = getComputedStyle(
                root
            ).getPropertyValue(`--fw-${font_family}`);
        });

        VARIANTS.forEach((variant) => {
            SHADES.forEach((shade) => {
                const hex = getComputedStyle(root)
                    .getPropertyValue(`--${variant}-${shade}`)
                    .toUpperCase()
                    .trim();
                saved_colors.palette[`${variant}-${shade}`] = hex;
            });
        });

        BUTTON_VARIANTS.forEach((variant) => {
            BUTTON_STATES.forEach((state) => {
                let style = getComputedStyle(root).getPropertyValue(
                    `--button-${variant}-${state}`
                );
                if (style.startsWith('#')) {
                    const found = Object.entries(saved_colors.palette).find(
                        ([, value]) => value === style.toUpperCase().trim()
                    );
                    if (found) {
                        const [key] = found;
                        style = `var(--${key})`;
                    }
                }

                let actualHex = '';
                const varMatch = style.match(/var\(--([^)]+)\)/);
                if (varMatch) {
                    const key = varMatch[1];
                    actualHex =
                        saved_colors.palette[key] ||
                        getComputedStyle(root)
                            .getPropertyValue(`--${key}`)
                            .toUpperCase()
                            .trim();
                } else if (style.startsWith('#')) {
                    actualHex = style.toUpperCase().trim();
                }

                const textColorRaw = getComputedStyle(root)
                    .getPropertyValue(`--button-text-${variant}-${state}`)
                    .trim();
                const textColor =
                    normalizeHexColor(textColorRaw) ||
                    (actualHex ? pickTextColor(actualHex) : 'white');

                const normalizedStyle = normalizeButtonFillSource(style);
                const solidFallback = `var(--${variant}-${
                    state === 'hover' ? '600' : '700'
                })`;
                const solidToken = getButtonFillSolidToken(
                    normalizedStyle,
                    solidFallback
                );
                saved_colors.button[`${variant}-${state}`] = normalizedStyle;
                saved_colors.button[`${variant}-${state}-solid`] = solidToken;
                saved_colors.button[`text-${variant}-${state}`] = textColor;
                if (normalizedStyle !== style.trim()) {
                    setCssVar(`--button-${variant}-${state}`, normalizedStyle);
                }
                setCssVar(`--button-${variant}-${state}-solid`, solidToken);
                if (!textColorRaw || textColorRaw !== textColor) {
                    setCssVar(`--button-text-${variant}-${state}`, textColor);
                }
            });
        });

        const borderWidthRaw = getComputedStyle(root)
            .getPropertyValue(`--button-border-width`)
            .trim();
        const borderColorRaw = getComputedStyle(root)
            .getPropertyValue(`--button-border-color`)
            .trim();
        const primaryBorderColorRaw = getComputedStyle(root)
            .getPropertyValue(`--button-primary-border-color`)
            .trim();
        const primaryBorderDefaultRaw = getComputedStyle(root)
            .getPropertyValue(`--button-primary-border-default`)
            .trim();
        const primaryBorderHoverRaw = getComputedStyle(root)
            .getPropertyValue(`--button-primary-border-hover`)
            .trim();
        const secondaryBorderColorRaw = getComputedStyle(root)
            .getPropertyValue(`--button-secondary-border-color`)
            .trim();
        const secondaryBorderDefaultRaw = getComputedStyle(root)
            .getPropertyValue(`--button-secondary-border-default`)
            .trim();
        const secondaryBorderHoverRaw = getComputedStyle(root)
            .getPropertyValue(`--button-secondary-border-hover`)
            .trim();
        const primaryShadowDefaultRaw = getComputedStyle(root)
            .getPropertyValue(`--button-primary-shadow-default`)
            .trim();
        const primaryShadowHoverRaw = getComputedStyle(root)
            .getPropertyValue(`--button-primary-shadow-hover`)
            .trim();
        const secondaryShadowDefaultRaw = getComputedStyle(root)
            .getPropertyValue(`--button-secondary-shadow-default`)
            .trim();
        const secondaryShadowHoverRaw = getComputedStyle(root)
            .getPropertyValue(`--button-secondary-shadow-hover`)
            .trim();
        const primaryHoverAnimationRaw = getComputedStyle(root)
            .getPropertyValue(`--button-primary-hover-animation`)
            .trim();
        const secondaryHoverAnimationRaw = getComputedStyle(root)
            .getPropertyValue(`--button-secondary-hover-animation`)
            .trim();
        const primaryIconDefaultRaw = getComputedStyle(root)
            .getPropertyValue(`--button-primary-icon-default`)
            .trim();
        const primaryIconHoverRaw = getComputedStyle(root)
            .getPropertyValue(`--button-primary-icon-hover`)
            .trim();
        const secondaryIconDefaultRaw = getComputedStyle(root)
            .getPropertyValue(`--button-secondary-icon-default`)
            .trim();
        const secondaryIconHoverRaw = getComputedStyle(root)
            .getPropertyValue(`--button-secondary-icon-hover`)
            .trim();
        const primaryIconPositionRaw = getComputedStyle(root)
            .getPropertyValue(`--button-primary-icon-position`)
            .trim();
        const secondaryIconPositionRaw = getComputedStyle(root)
            .getPropertyValue(`--button-secondary-icon-position`)
            .trim();
        const primaryIconSizeRaw = getComputedStyle(root)
            .getPropertyValue(`--button-primary-icon-size`)
            .trim();
        const secondaryIconSizeRaw = getComputedStyle(root)
            .getPropertyValue(`--button-secondary-icon-size`)
            .trim();
        const resolvedPrimaryHoverAnimation =
            normalizeButtonHoverAnimationValue(primaryHoverAnimationRaw);
        const resolvedSecondaryHoverAnimation =
            normalizeButtonHoverAnimationValue(secondaryHoverAnimationRaw);
        const resolvedPrimaryIconDefault = normalizeButtonIconClassValue(
            primaryIconDefaultRaw
        );
        const resolvedPrimaryIconHover =
            normalizeButtonIconClassValue(primaryIconHoverRaw);
        const resolvedSecondaryIconDefault = normalizeButtonIconClassValue(
            secondaryIconDefaultRaw
        );
        const resolvedSecondaryIconHover = normalizeButtonIconClassValue(
            secondaryIconHoverRaw
        );
        const resolvedPrimaryIconPosition = ['left', 'right'].includes(
            String(primaryIconPositionRaw).toLowerCase()
        )
            ? String(primaryIconPositionRaw).toLowerCase()
            : 'right';
        const resolvedSecondaryIconPosition = ['left', 'right'].includes(
            String(secondaryIconPositionRaw).toLowerCase()
        )
            ? String(secondaryIconPositionRaw).toLowerCase()
            : 'right';
        const resolvedPrimaryIconSize =
            normalizeButtonIconSizeValue(primaryIconSizeRaw);
        const resolvedSecondaryIconSize =
            normalizeButtonIconSizeValue(secondaryIconSizeRaw);
        const resolvedBorderWidth = borderWidthRaw || '0px';
        const resolvedBaseBorderColor =
            normalizeBorderImageSource(borderColorRaw);
        let resolvedPrimaryBorderDefault = normalizeBorderImageSource(
            primaryBorderDefaultRaw || primaryBorderColorRaw || borderColorRaw
        );
        let resolvedPrimaryBorderHover = normalizeBorderImageSource(
            primaryBorderHoverRaw ||
                primaryBorderDefaultRaw ||
                primaryBorderColorRaw ||
                borderColorRaw
        );
        let resolvedSecondaryBorderDefault = normalizeBorderImageSource(
            secondaryBorderDefaultRaw ||
                secondaryBorderColorRaw ||
                borderColorRaw
        );
        let resolvedSecondaryBorderHover = normalizeBorderImageSource(
            secondaryBorderHoverRaw ||
                secondaryBorderDefaultRaw ||
                secondaryBorderColorRaw ||
                borderColorRaw
        );
        if (
            !primaryBorderDefaultRaw ||
            primaryBorderDefaultRaw === 'transparent' ||
            isLegacyPrimaryBorderDefault(primaryBorderDefaultRaw) ||
            (!primaryBorderDefaultRaw &&
                isLegacyPrimaryBorderDefault(primaryBorderColorRaw))
        ) {
            resolvedPrimaryBorderDefault =
                DEFAULT_PRIMARY_BORDER_DEFAULT_GRADIENT;
        }
        if (
            !primaryBorderHoverRaw ||
            primaryBorderHoverRaw === 'transparent' ||
            isLegacyPrimaryBorderDefault(primaryBorderHoverRaw)
        ) {
            resolvedPrimaryBorderHover = DEFAULT_PRIMARY_BORDER_HOVER_GRADIENT;
        }
        if (
            !secondaryBorderDefaultRaw ||
            secondaryBorderDefaultRaw === 'transparent' ||
            isLegacySecondaryBorderDefault(secondaryBorderDefaultRaw) ||
            (!secondaryBorderDefaultRaw &&
                isLegacySecondaryBorderDefault(secondaryBorderColorRaw))
        ) {
            resolvedSecondaryBorderDefault =
                DEFAULT_SECONDARY_BORDER_DEFAULT_GRADIENT;
        }
        if (
            !secondaryBorderHoverRaw ||
            secondaryBorderHoverRaw === 'transparent' ||
            isLegacySecondaryBorderDefault(secondaryBorderHoverRaw)
        ) {
            resolvedSecondaryBorderHover =
                DEFAULT_SECONDARY_BORDER_HOVER_GRADIENT;
        }
        saved_colors.button['border-width'] = borderWidthRaw || '0px';
        saved_colors.button['border-color'] = resolvedBaseBorderColor;
        saved_colors.button['primary-border-color'] =
            resolvedPrimaryBorderDefault;
        saved_colors.button['secondary-border-color'] =
            resolvedSecondaryBorderDefault;
        saved_colors.button['primary-border-default'] =
            resolvedPrimaryBorderDefault;
        saved_colors.button['primary-border-hover'] =
            resolvedPrimaryBorderHover;
        saved_colors.button['secondary-border-default'] =
            resolvedSecondaryBorderDefault;
        saved_colors.button['secondary-border-hover'] =
            resolvedSecondaryBorderHover;
        saved_colors.button['primary-shadow-default'] =
            primaryShadowDefaultRaw || 'none';
        saved_colors.button['primary-shadow-hover'] =
            primaryShadowHoverRaw || 'none';
        saved_colors.button['secondary-shadow-default'] =
            secondaryShadowDefaultRaw || 'none';
        saved_colors.button['secondary-shadow-hover'] =
            secondaryShadowHoverRaw || 'none';
        saved_colors.button['primary-hover-animation'] =
            resolvedPrimaryHoverAnimation;
        saved_colors.button['secondary-hover-animation'] =
            resolvedSecondaryHoverAnimation;
        saved_colors.button['primary-icon-default'] =
            resolvedPrimaryIconDefault;
        saved_colors.button['primary-icon-hover'] = resolvedPrimaryIconHover;
        saved_colors.button['secondary-icon-default'] =
            resolvedSecondaryIconDefault;
        saved_colors.button['secondary-icon-hover'] =
            resolvedSecondaryIconHover;
        saved_colors.button['primary-icon-position'] =
            resolvedPrimaryIconPosition;
        saved_colors.button['secondary-icon-position'] =
            resolvedSecondaryIconPosition;
        saved_colors.button['primary-icon-size'] = resolvedPrimaryIconSize;
        saved_colors.button['secondary-icon-size'] = resolvedSecondaryIconSize;

        if (!borderWidthRaw) {
            setCssVar(`--button-border-width`, resolvedBorderWidth);
        }
        if (!borderColorRaw || borderColorRaw !== resolvedBaseBorderColor) {
            setCssVar(`--button-border-color`, resolvedBaseBorderColor);
        }
        if (
            !primaryBorderDefaultRaw ||
            primaryBorderDefaultRaw !== resolvedPrimaryBorderDefault
        ) {
            setCssVar(
                `--button-primary-border-default`,
                resolvedPrimaryBorderDefault
            );
        }
        if (
            !primaryBorderHoverRaw ||
            primaryBorderHoverRaw !== resolvedPrimaryBorderHover
        ) {
            setCssVar(
                `--button-primary-border-hover`,
                resolvedPrimaryBorderHover
            );
        }
        if (
            !primaryBorderColorRaw ||
            primaryBorderColorRaw !== resolvedPrimaryBorderDefault
        ) {
            setCssVar(
                `--button-primary-border-color`,
                resolvedPrimaryBorderDefault
            );
        }
        if (
            !secondaryBorderDefaultRaw ||
            secondaryBorderDefaultRaw !== resolvedSecondaryBorderDefault
        ) {
            setCssVar(
                `--button-secondary-border-default`,
                resolvedSecondaryBorderDefault
            );
        }
        if (
            !secondaryBorderHoverRaw ||
            secondaryBorderHoverRaw !== resolvedSecondaryBorderHover
        ) {
            setCssVar(
                `--button-secondary-border-hover`,
                resolvedSecondaryBorderHover
            );
        }
        if (
            !secondaryBorderColorRaw ||
            secondaryBorderColorRaw !== resolvedSecondaryBorderDefault
        ) {
            setCssVar(
                `--button-secondary-border-color`,
                resolvedSecondaryBorderDefault
            );
        }
        if (!primaryShadowDefaultRaw) {
            setCssVar(`--button-primary-shadow-default`, 'none');
        }
        if (!primaryShadowHoverRaw) {
            setCssVar(`--button-primary-shadow-hover`, 'none');
        }
        if (!secondaryShadowDefaultRaw) {
            setCssVar(`--button-secondary-shadow-default`, 'none');
        }
        if (!secondaryShadowHoverRaw) {
            setCssVar(`--button-secondary-shadow-hover`, 'none');
        }
        if (
            !primaryHoverAnimationRaw ||
            primaryHoverAnimationRaw !== resolvedPrimaryHoverAnimation
        ) {
            setCssVar(
                `--button-primary-hover-animation`,
                resolvedPrimaryHoverAnimation
            );
        }
        if (
            !secondaryHoverAnimationRaw ||
            secondaryHoverAnimationRaw !== resolvedSecondaryHoverAnimation
        ) {
            setCssVar(
                `--button-secondary-hover-animation`,
                resolvedSecondaryHoverAnimation
            );
        }
        if (
            !primaryIconDefaultRaw ||
            primaryIconDefaultRaw !== resolvedPrimaryIconDefault
        ) {
            setCssVar(
                `--button-primary-icon-default`,
                resolvedPrimaryIconDefault
            );
        }
        if (
            !primaryIconHoverRaw ||
            primaryIconHoverRaw !== resolvedPrimaryIconHover
        ) {
            setCssVar(`--button-primary-icon-hover`, resolvedPrimaryIconHover);
        }
        if (
            !secondaryIconDefaultRaw ||
            secondaryIconDefaultRaw !== resolvedSecondaryIconDefault
        ) {
            setCssVar(
                `--button-secondary-icon-default`,
                resolvedSecondaryIconDefault
            );
        }
        if (
            !secondaryIconHoverRaw ||
            secondaryIconHoverRaw !== resolvedSecondaryIconHover
        ) {
            setCssVar(
                `--button-secondary-icon-hover`,
                resolvedSecondaryIconHover
            );
        }
        if (
            !primaryIconPositionRaw ||
            primaryIconPositionRaw.toLowerCase() !== resolvedPrimaryIconPosition
        ) {
            setCssVar(
                `--button-primary-icon-position`,
                resolvedPrimaryIconPosition
            );
        }
        if (
            !secondaryIconPositionRaw ||
            secondaryIconPositionRaw.toLowerCase() !==
                resolvedSecondaryIconPosition
        ) {
            setCssVar(
                `--button-secondary-icon-position`,
                resolvedSecondaryIconPosition
            );
        }
        if (
            !primaryIconSizeRaw ||
            primaryIconSizeRaw.toLowerCase() !== resolvedPrimaryIconSize
        ) {
            setCssVar(`--button-primary-icon-size`, resolvedPrimaryIconSize);
        }
        if (
            !secondaryIconSizeRaw ||
            secondaryIconSizeRaw.toLowerCase() !== resolvedSecondaryIconSize
        ) {
            setCssVar(
                `--button-secondary-icon-size`,
                resolvedSecondaryIconSize
            );
        }

        const readButtonVarWithFallback = (name, fallback) => {
            const val = getComputedStyle(root).getPropertyValue(name);
            return val?.trim() ? val.trim() : fallback;
        };
        const rawButtonSizingPreset = getComputedStyle(root)
            .getPropertyValue(`--button-size-preset`)
            .trim();
        const buttonSizingPreset =
            BUTTON_SIZING_PRESET_MAP.get(rawButtonSizingPreset)?.key ||
            DEFAULT_BUTTON_SIZING_PRESET_KEY;
        const sizingFallbackPreset =
            BUTTON_SIZING_PRESET_MAP.get(buttonSizingPreset) ||
            BUTTON_SIZING_PRESET_MAP.get(DEFAULT_BUTTON_SIZING_PRESET_KEY);

        const rawButtonFontScale = readButtonVarWithFallback(
            '--button-font-scale',
            '1'
        );
        const resolvedButtonFontScale = Number.isFinite(
            Number.parseFloat(rawButtonFontScale)
        )
            ? String(Number.parseFloat(rawButtonFontScale))
            : '1';

        const rawButtonFontWeight = readButtonVarWithFallback(
            '--button-font-weight',
            '700'
        );
        const resolvedButtonFontWeight = Number.isFinite(
            Number.parseInt(rawButtonFontWeight, 10)
        )
            ? String(Number.parseInt(rawButtonFontWeight, 10))
            : '700';

        const saved_buttonSizing = {
            preset: buttonSizingPreset,
            fontScale: resolvedButtonFontScale,
            fontWeight: resolvedButtonFontWeight,
            sm: {
                px: readButtonVarWithFallback(
                    '--button-sm-px',
                    sizingFallbackPreset.sm.px
                ),
                py: readButtonVarWithFallback(
                    '--button-sm-py',
                    sizingFallbackPreset.sm.py
                ),
                fontSize: readButtonVarWithFallback(
                    '--button-sm-font-size',
                    sizingFallbackPreset.sm.fontSize
                ),
                lineHeight: readButtonVarWithFallback(
                    '--button-sm-line-height',
                    sizingFallbackPreset.sm.lineHeight
                ),
            },
            md: {
                px: readButtonVarWithFallback(
                    '--button-md-px',
                    sizingFallbackPreset.md.px
                ),
                py: readButtonVarWithFallback(
                    '--button-md-py',
                    sizingFallbackPreset.md.py
                ),
                fontSize: readButtonVarWithFallback(
                    '--button-md-font-size',
                    sizingFallbackPreset.md.fontSize
                ),
                lineHeight: readButtonVarWithFallback(
                    '--button-md-line-height',
                    sizingFallbackPreset.md.lineHeight
                ),
            },
            lg: {
                px: readButtonVarWithFallback(
                    '--button-lg-px',
                    sizingFallbackPreset.lg.px
                ),
                py: readButtonVarWithFallback(
                    '--button-lg-py',
                    sizingFallbackPreset.lg.py
                ),
                fontSize: readButtonVarWithFallback(
                    '--button-lg-font-size',
                    sizingFallbackPreset.lg.fontSize
                ),
                lineHeight: readButtonVarWithFallback(
                    '--button-lg-line-height',
                    sizingFallbackPreset.lg.lineHeight
                ),
            },
            xl: {
                px: readButtonVarWithFallback(
                    '--button-xl-px',
                    sizingFallbackPreset.xl.px
                ),
                py: readButtonVarWithFallback(
                    '--button-xl-py',
                    sizingFallbackPreset.xl.py
                ),
                fontSize: readButtonVarWithFallback(
                    '--button-xl-font-size',
                    sizingFallbackPreset.xl.fontSize
                ),
                lineHeight: readButtonVarWithFallback(
                    '--button-xl-line-height',
                    sizingFallbackPreset.xl.lineHeight
                ),
            },
        };

        setSiteData((prev) => ({
            ...prev,
            fonts: saved_fonts,
            colors: saved_colors,
            fontWeights: saved_fontWeights,
            shape: { radius: saved_shape_radius },
            buttonSizing: saved_buttonSizing,
        }));

        setSavedSiteData((prev) => ({
            ...prev,
            fonts: saved_fonts,
            colors: saved_colors,
            fontWeights: saved_fontWeights,
            shape: { radius: saved_shape_radius },
            buttonSizing: saved_buttonSizing,
        }));
    }, []);

    // Load images
    useEffect(() => {
        let cancelled = false;

        const getImages = async () => {
            try {
                const data = await fetchCompanyImages(global_data.company_id);
                if (cancelled) return;

                setSiteData((prev) => ({ ...prev, images: data }));
                setSavedSiteData((prev) => ({ ...prev, images: data }));
            } catch (error) {
                console.log(error);
            }
        };

        getImages();
        return () => {
            cancelled = true;
        };
    }, []);

    const isSiteSettingsVisible = () => is_site_settings_open;

    const handleOpenSiteSettings = () => {
        setIsSiteSettingsOpen(true);
        setComponentEditorOpenIndex(null);
        setIsPageSettingsOpen(null);
        setAddComponentOpenIndex(null);
    };

    const handleCloseSiteSettings = () => setIsSiteSettingsOpen(false);

    const handleSiteDataChange = async ({ mode, data }) => {
        switch (mode) {
            case 'font':
                return handleFontChange(data);
            case 'font-weight':
                return handleFontWeightChange(data);
            case 'palette':
                return await handleColorPaletteChange(data);
            case 'button-color':
                return handleButtonColorChange(data);
            case 'button-sizing':
                return handleButtonSizingChange(data);
            case 'button-font-scale':
                return handleButtonFontScaleChange(data);
            case 'button-font-weight':
                return handleButtonFontWeightChange(data);
            case 'button-gradient':
                return handleButtonGradientChange(data);
            case 'button-text-color':
                return handleButtonTextColorChange(data);
            case 'button-border':
                return handleButtonBorderChange(data);
            case 'button-shadow':
                return handleButtonShadowChange(data);
            case 'button-hover-animation':
                return handleButtonHoverAnimationChange(data);
            case 'button-icon':
                return handleButtonIconChange(data);
            case 'button-icon-position':
                return handleButtonIconPositionChange(data);
            case 'button-icon-size':
                return handleButtonIconSizeChange(data);
            case 'shape-radius':
                return handleShapeRadiusChange(data);
            case 'google-tag':
                return handleGoogleTagChange(data);
            case 'icon-pack-override':
                return handleIconPackOverride(data);

            default:
                throw new Error('Invalid mode provided');
        }
    };

    const handleShapeRadiusChange = ({ value }) => {
        setSiteData((prev) => ({
            ...prev,
            shape: { ...prev.shape, radius: value },
        }));
        setCssVar(`--shape-radius`, value);
    };

    const handleButtonSizingChange = ({ preset }) => {
        const presetKey = String(preset || '').trim();
        const selectedPreset =
            BUTTON_SIZING_PRESET_MAP.get(presetKey) ||
            BUTTON_SIZING_PRESET_MAP.get(DEFAULT_BUTTON_SIZING_PRESET_KEY);

        const currentSmFontSize =
            readCssVar('--button-sm-font-size') ||
            site_data?.buttonSizing?.sm?.fontSize ||
            selectedPreset.sm.fontSize;
        const currentSmLineHeight =
            readCssVar('--button-sm-line-height') ||
            site_data?.buttonSizing?.sm?.lineHeight ||
            selectedPreset.sm.lineHeight;
        const currentMdFontSize =
            readCssVar('--button-md-font-size') ||
            site_data?.buttonSizing?.md?.fontSize ||
            selectedPreset.md.fontSize;
        const currentMdLineHeight =
            readCssVar('--button-md-line-height') ||
            site_data?.buttonSizing?.md?.lineHeight ||
            selectedPreset.md.lineHeight;
        const currentLgFontSize =
            readCssVar('--button-lg-font-size') ||
            site_data?.buttonSizing?.lg?.fontSize ||
            selectedPreset.lg.fontSize;
        const currentLgLineHeight =
            readCssVar('--button-lg-line-height') ||
            site_data?.buttonSizing?.lg?.lineHeight ||
            selectedPreset.lg.lineHeight;
        const currentXlFontSize =
            readCssVar('--button-xl-font-size') ||
            site_data?.buttonSizing?.xl?.fontSize ||
            selectedPreset.xl.fontSize;
        const currentXlLineHeight =
            readCssVar('--button-xl-line-height') ||
            site_data?.buttonSizing?.xl?.lineHeight ||
            selectedPreset.xl.lineHeight;
        const currentFontScale =
            readCssVar('--button-font-scale') ||
            site_data?.buttonSizing?.fontScale ||
            '1';
        const currentFontWeight =
            readCssVar('--button-font-weight') ||
            site_data?.buttonSizing?.fontWeight ||
            '700';

        const nextButtonSizing = {
            preset: selectedPreset.key,
            fontScale: currentFontScale,
            fontWeight: currentFontWeight,
            sm: {
                ...selectedPreset.sm,
                fontSize: currentSmFontSize,
                lineHeight: currentSmLineHeight,
            },
            md: {
                ...selectedPreset.md,
                fontSize: currentMdFontSize,
                lineHeight: currentMdLineHeight,
            },
            lg: {
                ...selectedPreset.lg,
                fontSize: currentLgFontSize,
                lineHeight: currentLgLineHeight,
            },
            xl: {
                ...selectedPreset.xl,
                fontSize: currentXlFontSize,
                lineHeight: currentXlLineHeight,
            },
        };

        setSiteData((prev) => ({
            ...prev,
            buttonSizing: nextButtonSizing,
        }));

        setCssVar(`--button-size-preset`, selectedPreset.key);
        setCssVar(`--button-sm-px`, selectedPreset.sm.px);
        setCssVar(`--button-sm-py`, selectedPreset.sm.py);
        setCssVar(`--button-sm-font-size`, currentSmFontSize);
        setCssVar(`--button-sm-line-height`, currentSmLineHeight);
        setCssVar(`--button-md-px`, selectedPreset.md.px);
        setCssVar(`--button-md-py`, selectedPreset.md.py);
        setCssVar(`--button-md-font-size`, currentMdFontSize);
        setCssVar(`--button-md-line-height`, currentMdLineHeight);
        setCssVar(`--button-lg-px`, selectedPreset.lg.px);
        setCssVar(`--button-lg-py`, selectedPreset.lg.py);
        setCssVar(`--button-lg-font-size`, currentLgFontSize);
        setCssVar(`--button-lg-line-height`, currentLgLineHeight);
        setCssVar(`--button-xl-px`, selectedPreset.xl.px);
        setCssVar(`--button-xl-py`, selectedPreset.xl.py);
        setCssVar(`--button-xl-font-size`, currentXlFontSize);
        setCssVar(`--button-xl-line-height`, currentXlLineHeight);
        setCssVar(`--button-font-scale`, currentFontScale);
        setCssVar(`--button-font-weight`, currentFontWeight);
    };

    const handleButtonFontScaleChange = ({ fontScale }) => {
        const parsed =
            typeof fontScale === 'number'
                ? fontScale
                : Number.parseFloat(String(fontScale));
        if (!Number.isFinite(parsed)) return;

        const value = String(parsed);
        setSiteData((prev) => ({
            ...prev,
            buttonSizing: { ...prev.buttonSizing, fontScale: value },
        }));
        setCssVar(`--button-font-scale`, value);
    };

    const handleButtonFontWeightChange = ({ fontWeight }) => {
        const parsed =
            typeof fontWeight === 'number'
                ? fontWeight
                : Number.parseInt(String(fontWeight), 10);
        if (!Number.isFinite(parsed)) return;

        const value = String(parsed);
        setSiteData((prev) => ({
            ...prev,
            buttonSizing: { ...prev.buttonSizing, fontWeight: value },
        }));
        setCssVar(`--button-font-weight`, value);
    };

    const handleGoogleTagChange = ({ tag }) => {
        setSiteData((prev) => ({
            ...prev,
            google_tag: tag,
        }));
    };

    const handleFontChange = ({ type, font }) => {
        setSiteData((prev) => ({
            ...prev,
            fonts: { ...prev.fonts, [type]: font },
        }));
        setCssVar(`--ff-${type}`, `"${font}"`);
    };

    const handleFontWeightChange = ({ type, weight }) => {
        setSiteData((prev) => ({
            ...prev,
            fontWeights: { ...prev.fontWeights, [type]: weight },
        }));
        setCssVar(`--fw-${type}`, weight);
    };

    const handleColorPaletteChange = async ({
        variant,
        color: input_color,
    }) => {
        const color = input_color.toUpperCase();
        if (!new RegExp(RBG_HEX_REGEX).test(color)) return;

        setRegeneratingPalette((prev) => ({ ...prev, [variant]: true }));

        try {
            const data = await API.get('theme/palette', {
                params: { hex: color },
            });

            const new_colors = {};
            Object.entries(data.colors).forEach(([shade, color_value]) => {
                const hex = String(color_value).toUpperCase();
                new_colors[`${variant}-${shade}`] = hex;
                setCssVar(`--${variant}-${shade}`, hex);
            });

            const previousPalette = site_data?.colors?.palette || {};
            const previousButton = site_data?.colors?.button || {};
            const buttonPaletteLinkedUpdates = {};
            const cssVarUpdates = [];

            BUTTON_STATES.forEach((stateKey) => {
                const fillVarName = `--button-${variant}-${stateKey}`;
                const fillDataKey = `${variant}-${stateKey}`;
                const currentFill =
                    readCssVar(fillVarName) ||
                    previousButton[fillDataKey] ||
                    '';
                const fillShade = inferThemeShadeFromSource(
                    variant,
                    currentFill,
                    previousPalette
                );
                if (fillShade && new_colors[`${variant}-${fillShade}`]) {
                    const token = `var(--${variant}-${fillShade})`;
                    const nextFill = `linear-gradient(${token}, ${token})`;
                    buttonPaletteLinkedUpdates[fillDataKey] = nextFill;
                    buttonPaletteLinkedUpdates[`${fillDataKey}-solid`] = token;
                    buttonPaletteLinkedUpdates[`text-${variant}-${stateKey}`] =
                        pickTextColor(new_colors[`${variant}-${fillShade}`]);
                    cssVarUpdates.push([fillVarName, nextFill]);
                    cssVarUpdates.push([
                        `--button-${variant}-${stateKey}-solid`,
                        token,
                    ]);
                    cssVarUpdates.push([
                        `--button-text-${variant}-${stateKey}`,
                        pickTextColor(new_colors[`${variant}-${fillShade}`]),
                    ]);
                }

                const borderVarName = `--button-${variant}-border-${stateKey}`;
                const borderDataKey = `${variant}-border-${stateKey}`;
                const currentBorder =
                    readCssVar(borderVarName) ||
                    previousButton[borderDataKey] ||
                    (stateKey === 'default'
                        ? previousButton[`${variant}-border-color`] || ''
                        : '');
                const borderShade = inferThemeShadeFromSource(
                    variant,
                    currentBorder,
                    previousPalette
                );
                if (borderShade && new_colors[`${variant}-${borderShade}`]) {
                    const token = `var(--${variant}-${borderShade})`;
                    const nextBorder = `linear-gradient(${token}, ${token})`;
                    buttonPaletteLinkedUpdates[borderDataKey] = nextBorder;
                    cssVarUpdates.push([borderVarName, nextBorder]);
                    if (stateKey === 'default') {
                        buttonPaletteLinkedUpdates[`${variant}-border-color`] =
                            nextBorder;
                        cssVarUpdates.push([
                            `--button-${variant}-border-color`,
                            nextBorder,
                        ]);
                    }
                }

                const shadowVarName = `--button-${variant}-shadow-${stateKey}`;
                const shadowDataKey = `${variant}-shadow-${stateKey}`;
                const currentShadow =
                    readCssVar(shadowVarName) ||
                    previousButton[shadowDataKey] ||
                    'none';
                if (isShadowEnabledValue(currentShadow)) {
                    const shadowFillSource =
                        buttonPaletteLinkedUpdates[fillDataKey] || currentFill;
                    const nextShadow = buildButtonShadowValue(
                        shadowFillSource,
                        readCssVar,
                        { ...previousPalette, ...new_colors },
                        buttonPaletteLinkedUpdates[`${fillDataKey}-solid`] ||
                            previousButton[`${fillDataKey}-solid`] ||
                            ''
                    );
                    buttonPaletteLinkedUpdates[shadowDataKey] = nextShadow;
                    cssVarUpdates.push([shadowVarName, nextShadow]);
                }
            });

            cssVarUpdates.forEach(([name, value]) => setCssVar(name, value));

            setSiteData((prev) => ({
                ...prev,
                colors: {
                    ...prev.colors,
                    palette: { ...prev.colors.palette, ...new_colors },
                    button: {
                        ...prev.colors.button,
                        ...buttonPaletteLinkedUpdates,
                    },
                },
            }));
        } catch (error) {
            notifications.push({
                title: 'Failed to generate color palette!',
                variant: 'error',
                ttl: 20,
                error,
            });
        } finally {
            setRegeneratingPalette((prev) => ({ ...prev, [variant]: false }));
        }
    };

    const handleButtonColorChange = ({
        variant,
        state,
        key,
        hoverKey = null,
    }) => {
        const getHex = (tokenKey) => readCssVar(`--${tokenKey}`).toUpperCase();

        const defaultHex = getHex(key);
        const defaultText = pickTextColor(defaultHex);
        const defaultToken = `var(--${key})`;
        const defaultFill = `linear-gradient(${defaultToken}, ${defaultToken})`;
        const defaultBorder = `linear-gradient(${defaultToken}, ${defaultToken})`;
        const defaultShadowKey = `${variant}-shadow-${state}`;
        const defaultShadowVar = `--button-${variant}-shadow-${state}`;
        const palette = site_data?.colors?.palette || {};
        const previousDefaultShadow =
            readCssVar(defaultShadowVar) ||
            site_data?.colors?.button?.[defaultShadowKey] ||
            'none';
        const shouldUpdateDefaultShadow = isShadowEnabledValue(
            previousDefaultShadow
        );
        const nextDefaultShadow = shouldUpdateDefaultShadow
            ? buildButtonShadowValue(
                  defaultFill,
                  readCssVar,
                  palette,
                  defaultToken
              )
            : previousDefaultShadow;

        setSiteData((prev) => ({
            ...prev,
            colors: {
                ...prev.colors,
                button: {
                    ...prev.colors.button,
                    [`${variant}-${state}`]: defaultFill,
                    [`${variant}-${state}-solid`]: defaultToken,
                    [`text-${variant}-${state}`]: defaultText,
                    [`${variant}-border-${state}`]: defaultBorder,
                    ...(shouldUpdateDefaultShadow
                        ? {
                              [defaultShadowKey]: nextDefaultShadow,
                          }
                        : {}),
                    ...(state === 'default'
                        ? {
                              [`${variant}-border-color`]: defaultBorder,
                          }
                        : {}),
                },
            },
        }));

        setCssVar(`--button-${variant}-${state}`, defaultFill);
        setCssVar(`--button-${variant}-${state}-solid`, defaultToken);
        setCssVar(`--button-text-${variant}-${state}`, defaultText);
        setCssVar(`--button-${variant}-border-${state}`, defaultBorder);
        if (shouldUpdateDefaultShadow) {
            setCssVar(defaultShadowVar, nextDefaultShadow);
        }
        if (state === 'default') {
            setCssVar(`--button-${variant}-border-color`, defaultBorder);
        }

        if (hoverKey) {
            const hoverHex = getHex(hoverKey);
            const hoverText = pickTextColor(hoverHex);
            const hoverToken = `var(--${hoverKey})`;
            const hoverFill = `linear-gradient(${hoverToken}, ${hoverToken})`;
            const hoverBorder = `linear-gradient(${hoverToken}, ${hoverToken})`;
            const hoverShadowKey = `${variant}-shadow-hover`;
            const hoverShadowVar = `--button-${variant}-shadow-hover`;
            const previousHoverShadow =
                readCssVar(hoverShadowVar) ||
                site_data?.colors?.button?.[hoverShadowKey] ||
                'none';
            const shouldUpdateHoverShadow =
                isShadowEnabledValue(previousHoverShadow);
            const nextHoverShadow = shouldUpdateHoverShadow
                ? buildButtonShadowValue(
                      hoverFill,
                      readCssVar,
                      palette,
                      hoverToken
                  )
                : previousHoverShadow;

            setSiteData((prev) => ({
                ...prev,
                colors: {
                    ...prev.colors,
                    button: {
                        ...prev.colors.button,
                        [`${variant}-hover`]: hoverFill,
                        [`${variant}-hover-solid`]: hoverToken,
                        [`text-${variant}-hover`]: hoverText,
                        [`${variant}-border-hover`]: hoverBorder,
                        ...(shouldUpdateHoverShadow
                            ? {
                                  [hoverShadowKey]: nextHoverShadow,
                              }
                            : {}),
                    },
                },
            }));

            setCssVar(`--button-${variant}-hover`, hoverFill);
            setCssVar(`--button-${variant}-hover-solid`, hoverToken);
            setCssVar(`--button-text-${variant}-hover`, hoverText);
            setCssVar(`--button-${variant}-border-hover`, hoverBorder);
            if (shouldUpdateHoverShadow) {
                setCssVar(hoverShadowVar, nextHoverShadow);
            }
        }
    };

    const handleButtonGradientChange = ({
        variant,
        state,
        angle,
        colors,
        textColor,
    }) => {
        const resolvedVariant =
            String(variant).toLowerCase() === 'secondary'
                ? 'secondary'
                : 'primary';
        const resolvedState =
            String(state).toLowerCase() === 'hover' ? 'hover' : 'default';

        const parsedAngle =
            typeof angle === 'number'
                ? angle
                : Number.parseFloat(String(angle ?? ''));
        const clampedAngle = Number.isFinite(parsedAngle)
            ? Math.max(0, Math.min(360, parsedAngle))
            : 45;

        const normalizedStops = (Array.isArray(colors) ? colors : [])
            .map((c) => normalizeHexColor(c))
            .filter((c) => typeof c === 'string');

        if (normalizedStops.length < 2) return;

        const gradient = `linear-gradient(${clampedAngle}deg, ${normalizedStops.join(
            ', '
        )})`;
        const solidToken = normalizedStops[0];

        const normalizedTextColor = normalizeHexColor(textColor);
        const shadowDataKey = `${resolvedVariant}-shadow-${resolvedState}`;
        const shadowVar = `--button-${resolvedVariant}-shadow-${resolvedState}`;
        const previousShadow =
            readCssVar(shadowVar) ||
            site_data?.colors?.button?.[shadowDataKey] ||
            'none';
        const shouldUpdateShadow = isShadowEnabledValue(previousShadow);
        const nextShadow = shouldUpdateShadow
            ? buildButtonShadowValue(
                  gradient,
                  readCssVar,
                  site_data?.colors?.palette || {},
                  solidToken
              )
            : previousShadow;

        setSiteData((prev) => ({
            ...prev,
            colors: {
                ...prev.colors,
                button: {
                    ...prev.colors.button,
                    [`${resolvedVariant}-${resolvedState}`]: gradient,
                    [`${resolvedVariant}-${resolvedState}-solid`]: solidToken,
                    ...(shouldUpdateShadow
                        ? {
                              [shadowDataKey]: nextShadow,
                          }
                        : {}),
                    ...(normalizedTextColor
                        ? {
                              [`text-${resolvedVariant}-${resolvedState}`]:
                                  normalizedTextColor,
                          }
                        : {}),
                },
            },
        }));

        setCssVar(`--button-${resolvedVariant}-${resolvedState}`, gradient);
        setCssVar(
            `--button-${resolvedVariant}-${resolvedState}-solid`,
            solidToken
        );
        if (shouldUpdateShadow) {
            setCssVar(shadowVar, nextShadow);
        }
        if (normalizedTextColor) {
            setCssVar(
                `--button-text-${resolvedVariant}-${resolvedState}`,
                normalizedTextColor
            );
        }
    };

    const handleButtonShadowChange = ({ variant, state, mode }) => {
        const resolvedVariant =
            String(variant).toLowerCase() === 'secondary'
                ? 'secondary'
                : 'primary';
        const resolvedState =
            String(state).toLowerCase() === 'hover' ? 'hover' : 'default';
        const normalizedMode =
            String(mode).toLowerCase() === 'shadow' ? 'shadow' : 'none';

        const fillValue =
            readCssVar(`--button-${resolvedVariant}-${resolvedState}`) ||
            site_data?.colors?.button?.[
                `${resolvedVariant}-${resolvedState}`
            ] ||
            '';
        const solidValue =
            readCssVar(`--button-${resolvedVariant}-${resolvedState}-solid`) ||
            site_data?.colors?.button?.[
                `${resolvedVariant}-${resolvedState}-solid`
            ] ||
            readCssVar(
                `--${resolvedVariant}-${
                    resolvedState === 'hover' ? '600' : '700'
                }`
            ) ||
            `var(--${resolvedVariant}-${
                resolvedState === 'hover' ? '600' : '700'
            })`;
        const nextShadowValue =
            normalizedMode === 'shadow'
                ? buildButtonShadowValue(
                      fillValue,
                      readCssVar,
                      site_data?.colors?.palette || {},
                      solidValue
                  )
                : 'none';
        const shadowDataKey = `${resolvedVariant}-shadow-${resolvedState}`;

        setSiteData((prev) => ({
            ...prev,
            colors: {
                ...prev.colors,
                button: {
                    ...prev.colors.button,
                    [shadowDataKey]: nextShadowValue,
                },
            },
        }));
        setCssVar(
            `--button-${resolvedVariant}-shadow-${resolvedState}`,
            nextShadowValue
        );
    };

    const handleButtonHoverAnimationChange = ({ variant, animation }) => {
        const resolvedVariant =
            String(variant).toLowerCase() === 'secondary'
                ? 'secondary'
                : 'primary';
        const resolvedAnimation = normalizeButtonHoverAnimationValue(animation);

        setSiteData((prev) => ({
            ...prev,
            colors: {
                ...prev.colors,
                button: {
                    ...prev.colors.button,
                    [`${resolvedVariant}-hover-animation`]: resolvedAnimation,
                },
            },
        }));

        setCssVar(
            `--button-${resolvedVariant}-hover-animation`,
            resolvedAnimation
        );
    };

    const handleButtonIconChange = ({ variant, state, icon }) => {
        const resolvedVariant =
            String(variant).toLowerCase() === 'secondary'
                ? 'secondary'
                : 'primary';
        const resolvedState =
            String(state).toLowerCase() === 'hover' ? 'hover' : 'default';
        const normalizedIcon = normalizeButtonIconClassValue(icon);
        const dataKey = `${resolvedVariant}-icon-${resolvedState}`;

        setSiteData((prev) => ({
            ...prev,
            colors: {
                ...prev.colors,
                button: {
                    ...prev.colors.button,
                    [dataKey]: normalizedIcon,
                },
            },
        }));

        setCssVar(
            `--button-${resolvedVariant}-icon-${resolvedState}`,
            normalizedIcon
        );
    };

    const handleButtonIconPositionChange = ({ variant, position }) => {
        const resolvedVariant =
            String(variant).toLowerCase() === 'secondary'
                ? 'secondary'
                : 'primary';
        const resolvedPosition =
            String(position).toLowerCase() === 'left' ? 'left' : 'right';
        const dataKey = `${resolvedVariant}-icon-position`;

        setSiteData((prev) => ({
            ...prev,
            colors: {
                ...prev.colors,
                button: {
                    ...prev.colors.button,
                    [dataKey]: resolvedPosition,
                },
            },
        }));

        setCssVar(
            `--button-${resolvedVariant}-icon-position`,
            resolvedPosition
        );
    };

    const handleButtonIconSizeChange = ({ variant, size }) => {
        const resolvedVariant =
            String(variant).toLowerCase() === 'secondary'
                ? 'secondary'
                : 'primary';
        const resolvedSize = normalizeButtonIconSizeValue(size);
        const dataKey = `${resolvedVariant}-icon-size`;

        setSiteData((prev) => ({
            ...prev,
            colors: {
                ...prev.colors,
                button: {
                    ...prev.colors.button,
                    [dataKey]: resolvedSize,
                },
            },
        }));

        setCssVar(`--button-${resolvedVariant}-icon-size`, resolvedSize);
    };

    const handleButtonTextColorChange = ({ variant, state, color }) => {
        const resolvedVariant =
            String(variant).toLowerCase() === 'secondary'
                ? 'secondary'
                : 'primary';
        const resolvedState =
            String(state).toLowerCase() === 'hover' ? 'hover' : 'default';

        const normalized = normalizeHexColor(color);
        if (!normalized) return;

        setSiteData((prev) => ({
            ...prev,
            colors: {
                ...prev.colors,
                button: {
                    ...prev.colors.button,
                    [`text-${resolvedVariant}-${resolvedState}`]: normalized,
                },
            },
        }));

        setCssVar(
            `--button-text-${resolvedVariant}-${resolvedState}`,
            normalized
        );
    };

    const handleButtonBorderChange = ({
        variant,
        state,
        width,
        color,
        colors,
        angle,
    }) => {
        const parsedWidth =
            typeof width === 'number'
                ? width
                : Number.parseInt(String(width ?? ''), 10);
        const hasWidth = Number.isFinite(parsedWidth);

        const clampedWidth = hasWidth
            ? Math.max(0, Math.min(5, parsedWidth))
            : 0;
        const widthValue = hasWidth ? `${clampedWidth}px` : null;

        const normalizedStops = (Array.isArray(colors) ? colors : [])
            .map((entry) => normalizeHexColor(entry))
            .filter((entry) => typeof entry === 'string');

        const parsedAngle =
            typeof angle === 'number'
                ? angle
                : Number.parseFloat(String(angle ?? ''));
        const clampedAngle = Number.isFinite(parsedAngle)
            ? Math.max(0, Math.min(360, parsedAngle))
            : 45;

        const parsedSingleColor = normalizeHexColor(color);
        const hasColorInput = normalizedStops.length > 0 || !!parsedSingleColor;
        const normalizedColor =
            normalizedStops.length >= 2
                ? `linear-gradient(${clampedAngle}deg, ${normalizedStops.join(
                      ', '
                  )})`
                : parsedSingleColor
                ? `linear-gradient(${clampedAngle}deg, ${parsedSingleColor}, ${parsedSingleColor})`
                : normalizedStops[0]
                ? `linear-gradient(${clampedAngle}deg, ${normalizedStops[0]}, ${normalizedStops[0]})`
                : TRANSPARENT_BORDER_GRADIENT;

        const resolvedVariant =
            String(variant).toLowerCase() === 'secondary'
                ? 'secondary'
                : String(variant).toLowerCase() === 'primary'
                ? 'primary'
                : null;
        const resolvedState =
            String(state).toLowerCase() === 'hover' ? 'hover' : 'default';

        if (!hasWidth && !hasColorInput) return;

        setSiteData((prev) => ({
            ...prev,
            colors: {
                ...prev.colors,
                button: {
                    ...prev.colors.button,
                    ...(hasWidth
                        ? {
                              'border-width': widthValue,
                          }
                        : {}),
                    ...(resolvedVariant && hasColorInput
                        ? {
                              [`${resolvedVariant}-border-${resolvedState}`]:
                                  normalizedColor,
                              ...(resolvedState === 'default'
                                  ? {
                                        [`${resolvedVariant}-border-color`]:
                                            normalizedColor,
                                    }
                                  : {}),
                          }
                        : hasColorInput
                        ? {
                              'border-color': normalizedColor,
                              'primary-border-color': normalizedColor,
                              'secondary-border-color': normalizedColor,
                              'primary-border-default': normalizedColor,
                              'primary-border-hover': normalizedColor,
                              'secondary-border-default': normalizedColor,
                              'secondary-border-hover': normalizedColor,
                          }
                        : {}),
                },
            },
        }));

        if (hasWidth) {
            setCssVar(`--button-border-width`, widthValue);
        }
        if (resolvedVariant && hasColorInput) {
            setCssVar(
                `--button-${resolvedVariant}-border-${resolvedState}`,
                normalizedColor
            );
            if (resolvedState === 'default') {
                setCssVar(
                    `--button-${resolvedVariant}-border-color`,
                    normalizedColor
                );
            }
        } else if (hasColorInput) {
            setCssVar(`--button-border-color`, normalizedColor);
            setCssVar(`--button-primary-border-color`, normalizedColor);
            setCssVar(`--button-secondary-border-color`, normalizedColor);
            setCssVar(`--button-primary-border-default`, normalizedColor);
            setCssVar(`--button-primary-border-hover`, normalizedColor);
            setCssVar(`--button-secondary-border-default`, normalizedColor);
            setCssVar(`--button-secondary-border-hover`, normalizedColor);
        }
    };

    const handleAddImage = (image) => {
        const new_images = structuredClone(site_data.images);
        new_images.push(image);
        setSiteData((prev) => ({ ...prev, images: new_images }));
        setSavedSiteData((prev) => ({ ...prev, images: new_images }));
    };

    const handleIconPackOverride = ({ pack, style }) => {
        setSiteData((prev) => ({
            ...prev,
            iconPackOverride: pack || 'classic',
            iconStyleOverride: style || 'solid',
        }));
    };

    async function handleDeleteImage({ image_id }) {
        const new_images = structuredClone(site_data.images);
        const image_index = new_images.findIndex(
            (img) => img.image_id === image_id
        );
        const [deleted_image] = new_images.splice(image_index, 1);

        setSiteData((prev) => ({ ...prev, images: new_images }));
        setSavedSiteData((prev) => ({ ...prev, images: new_images }));

        async function deleteImage() {
            try {
                const response = await fetch(
                    `https://image-search-ytnjkd5upa-uc.a.run.app/images/${global_data.company_id}/${deleted_image.filename}`,
                    { method: 'DELETE' }
                );
                if (!response.ok) {
                    throw new Error(
                        `Failed to delete image ${response.status} ${response.statusText}`
                    );
                }
            } catch (error) {
                console.error(error);
            }
        }

        deleteImage();
    }

    const handleUndoSiteDataChanges = () => setSiteData(saved_site_data);

    const isRegeneratingPalette = ({ variant }) =>
        !!regenerating_palette[variant];

    //==========================================================================//
    //============================== PAGE CONTEXT ==============================//
    //==========================================================================//

    const isPageSettingsVisible = ({ mode }) =>
        page_settings_open_mode === mode;

    const handleOpenPageSettings = ({ mode }) => {
        setIsPageSettingsOpen(mode);
        setComponentEditorOpenIndex(null);
        setIsSiteSettingsOpen(false);
        setAddComponentOpenIndex(null);
    };

    const handleClosePageSettings = () => setIsPageSettingsOpen(null);

    const handlePageDataChange = ({ key, value }) => {
        setPageData((prev) => ({ ...prev, [key]: value }));
    };

    const handlePagePathChange = (value) => {
        setPagePath(sanitizePagePathInput(value));
    };

    const handleUndoPageDataChanges = () => {
        setPageData(saved_page_data);
        setPagePath(saved_page_path);
    };

    const handleAddPage = async ({ page_type, path }) => {
        setAddingPage(true);
        try {
            await API.post(`${global_data.company_id}/pages`, {
                page: page_type,
                path,
            });

            notifications.push({
                title: 'Page successfully added!',
                message:
                    'Wait for the site to be rebuilt and refresh the page.',
                variant: 'success',
                ttl: 10,
            });
        } catch (error) {
            notifications.push({
                title: 'Failed to add page!',
                variant: 'error',
                ttl: 20,
                error,
            });
        } finally {
            setAddingPage(false);
            if (page_settings_open_mode === 'add') handleClosePageSettings();
        }
    };

    const handleDeletePage = async () => {
        setDeletingPage(true);
        try {
            await API.delete(`${global_data.company_id}/pages`, {
                path: page_name,
            });

            notifications.push({
                title: 'Page successfully deleted!',
                message:
                    'Wait for the site to be rebuilt and refresh the page.',
                variant: 'success',
                ttl: 10,
            });
        } catch (error) {
            notifications.push({
                title: 'Failed to delete page!',
                variant: 'error',
                ttl: 20,
                error,
            });
        } finally {
            setDeletingPage(false);
        }
    };

    //==========================================================================//
    //=============================== COMPONENTS ===============================//
    //==========================================================================//

    const handleComponentDataChange = ({ file, key, value }) => {
        setComponents((prev_components) => {
            const index = prev_components.findIndex(
                (component) => component.data_file === file
            );
            const component = prev_components[index];
            const updated_component = structuredClone(component);

            if (key) {
                const keys = key.replace(/\[(\d+)\]/g, '.$1').split('.');
                let current = updated_component.props;
                for (const k of keys.slice(0, -1)) current = current[k];
                current[keys[keys.length - 1]] = value;
            } else {
                updated_component.props = {
                    ...updated_component.props,
                    ...value,
                };
            }

            const new_components = structuredClone(prev_components);
            new_components[index] = updated_component;
            return new_components;
        });
    };

    const handleGenerateComponentData = async ({
        file,
        component_name,
        key,
        prompt,
        temperature,
        use_gpt4,
        include_page_context,
    }) => {
        setComponentGenerating((prev) => ({
            ...prev,
            [`${file}-${key}`]: true,
        }));

        try {
            const data = await API.post(
                `${global_data.company_id}/content/generate`,
                {
                    page_name,
                    component_name,
                    component_data: components.find((c) => c.data_file === file)
                        .props,
                    key,
                    prompt,
                    temperature,
                    use_gpt4,
                    include_page_context,
                }
            );

            handleComponentDataChange({
                file,
                key,
                value: key ? data[key] : data,
            });

            notifications.push({
                title: 'Content successfully generated!',
                message: `Component: ${component_name} - Field: ${key}`,
                variant: 'success',
                ttl: 10,
            });
        } catch (error) {
            notifications.push({
                title: 'Failed to generate content!',
                message: `Component: ${component_name} - Field: ${key}`,
                variant: 'error',
                ttl: 20,
                error,
            });
        } finally {
            setComponentGenerating((prev) => ({
                ...prev,
                [`${file}-${key}`]: false,
            }));
        }
    };

    const isComponentGeneratingData = ({ file, key }) =>
        component_generating[`${file}-${key}`];

    const handleMoveComponent = ({ index, above_or_below }) => {
        if (index === 1 && above_or_below === -1) return;
        if (index === components.length - 2 && above_or_below === 1) return;

        const new_components = [...components];
        const component = new_components.splice(index, 1)[0];
        new_components.splice(index + above_or_below, 0, component);
        setComponents(new_components);
    };

    const handleAddComponent = async ({ index, component_name }) => {
        let schema = {};
        try {
            schema =
                await require(`../../../components/${component_name}.schema.json`);
        } catch (e) {
            // no schema file: fine
        }

        const default_props = Object.keys(schema).reduce((props, prop_name) => {
            props[prop_name] = schema[prop_name].value;
            return props;
        }, {});

        const file_name = `${component_name.toLowerCase()}-${nanoid()}`;

        const new_components = [...components];
        const new_component = {
            data_file: `${file_name}.json`,
            name: component_name,
            props: default_props,
        };

        new_components.splice(index, 0, new_component);
        setComponents(new_components);
        setAddComponentOpenIndex(null);
    };

    const handleDuplicateComponent = ({ index }) => {
        const new_components = [...components];
        const component = new_components[index];
        if (!component) return;

        const file_name = `${component.name.toLowerCase()}-${nanoid()}`;
        const duplicated_component = {
            data_file: `${file_name}.json`,
            name: component.name,
            props: structuredClone(component.props),
        };

        new_components.splice(index + 1, 0, duplicated_component);
        setComponents(new_components);
    };

    const handleDeleteComponent = ({ index }) => {
        const new_components = [...components];
        new_components.splice(index, 1);
        setComponents(new_components);
    };

    const handleUndoComponentDataChanges = () => {
        setComponentEditorOpenIndex(null);
        setAddComponentOpenIndex(null);
        setComponents(saved_components);
    };

    const handleOpenComponentEditor = ({ index }) => {
        setComponentEditorOpenIndex(index);
        setAddComponentOpenIndex(null);
        setIsSiteSettingsOpen(false);
        setIsPageSettingsOpen(null);
    };

    const handleCloseComponentEditor = () => setComponentEditorOpenIndex(null);

    const isComponentEditorVisible = ({ index }) =>
        index === component_editor_open_index;

    const handleOpenAddComponentPanel = ({ index }) => {
        setAddComponentOpenIndex(index);
        setComponentEditorOpenIndex(null);
        setIsSiteSettingsOpen(false);
        setIsPageSettingsOpen(null);
    };

    const handleCloseAddComponentPanel = () => setAddComponentOpenIndex(null);

    const isAddComponentPanelVisible = () => add_component_open_index !== null;

    //==========================================================================//
    //============================ NESTED COMPONENTS ===========================//
    //==========================================================================//

    const handleAddComponentNestedData = ({ file, key, index }) => {
        setComponents((prev) => {
            const component_index = prev.findIndex((c) => c.data_file === file);
            const component = prev[component_index];
            const updated = structuredClone(component);

            const keys = key.replace(/\[(\d+)\]/g, '.$1').split('.');
            let current = updated.props;
            for (const k of keys) current = current[k];

            const cloneOf = structuredClone(current[Math.max(index - 1, 0)]);
            current.splice(index, 0, cloneOf);

            const next = [...prev];
            next[component_index] = updated;
            return next;
        });
    };

    const handleDeleteComponentNestedData = ({ file, key }) => {
        setComponents((prev) => {
            const component_index = prev.findIndex((c) => c.data_file === file);
            const component = prev[component_index];
            const updated = structuredClone(component);

            const keys = key.replace(/\[(\d+)\]/g, '.$1').split('.');
            let parent = updated.props;
            for (const k of keys.slice(0, -1)) parent = parent[k];

            const idx = Number.parseInt(keys[keys.length - 1], 10);
            parent.splice(idx, 1);

            const next = [...prev];
            next[component_index] = updated;
            return next;
        });
    };

    const handleMoveComponentNestedData = ({ file, key, above_or_below }) => {
        setComponents((prev) => {
            const component_index = prev.findIndex((c) => c.data_file === file);
            const component = prev[component_index];
            const updated = structuredClone(component);

            const keys = key.replace(/\[(\d+)\]/g, '.$1').split('.');
            let parent = updated.props;
            for (const k of keys.slice(0, -1)) parent = parent[k];

            const idx = Number.parseInt(keys[keys.length - 1], 10);

            if (
                (idx === 0 && above_or_below === -1) ||
                (idx === parent.length - 1 && above_or_below === 1)
            ) {
                return prev;
            }

            const item = parent.splice(idx, 1)[0];
            parent.splice(idx + above_or_below, 0, item);

            const next = [...prev];
            next[component_index] = updated;
            return next;
        });
    };

    //==========================================================================//
    //================================= COMMON =================================//
    //==========================================================================//

    const handleSaveTheme = async () => {
        if (JSON.stringify(site_data) === JSON.stringify(saved_site_data)) {
            console.log('No theme changes to save');
            return;
        }

        try {
            await API.post(`${global_data.company_id}/theme`, {
                colors: site_data.colors,
                fonts: site_data.fonts,
                fontWeights: site_data.fontWeights,
                shape: site_data.shape,
                buttonSizing: site_data.buttonSizing,
            });

            notifications.push({
                title: 'Theme successfully updated!',
                variant: 'success',
                ttl: 10,
            });

            setSavedSiteData(site_data);
        } catch (error) {
            notifications.push({
                title: 'Failed to update theme!',
                variant: 'error',
                ttl: 10,
                error,
            });
        }
    };

    const syncUnpublishRedirectWithSiteSettings = async ({
        previous_page_path,
        next_page_path,
        is_published,
        redirect_target,
    }) => {
        const currentSource = normalizeRoutePath(next_page_path);
        const previousSource = normalizeRoutePath(previous_page_path);
        const managedSources = new Set([currentSource, previousSource]);

        const normalizedTarget =
            normalizeUnpublishRedirectTarget(redirect_target);
        const targetPathname = getTargetPathname(normalizedTarget);
        const shouldUpsert =
            !is_published &&
            !!normalizedTarget &&
            !!targetPathname &&
            targetPathname !== currentSource;

        try {
            const existingRules = await API.get(
                `${global_data.company_id}/redirects`
            );
            const existing = Array.isArray(existingRules) ? existingRules : [];

            const withoutManagedSources = existing.filter((rule) => {
                if (!rule || typeof rule !== 'object') return false;
                const source = normalizeRoutePath(rule.source);
                return !managedSources.has(source);
            });

            const nextRules = shouldUpsert
                ? [
                      ...withoutManagedSources,
                      {
                          source: currentSource,
                          destination: normalizedTarget,
                          permanent: true,
                      },
                  ]
                : withoutManagedSources;

            await API.post(`${global_data.company_id}/redirects`, {
                redirects: nextRules,
            });

            if (typeof window !== 'undefined') {
                window.dispatchEvent(
                    new CustomEvent('editor:redirects-updated', {
                        detail: nextRules,
                    })
                );
            }
        } catch (error) {
            notifications.push({
                title: 'Page saved, but redirect sync failed',
                message:
                    'Please verify Site Settings > Redirects for this page.',
                variant: 'error',
                ttl: 15,
                error,
            });
        }
    };

    const syncSitemapConfigWithTemplate = async () => {
        try {
            await API.post(`${global_data.company_id}/sitemap/update`);
        } catch (error) {
            notifications.push({
                title: 'Page saved, but sitemap sync failed',
                message:
                    'Please run Site Settings > Update Sitemap Config, then deploy again.',
                variant: 'error',
                ttl: 15,
                error,
            });
        }
    };

    const handleSaveToJSONFile = async () => {
        let data = {};
        const normalized_page_path = finalizePagePath(page_path);
        const attempted_page_path_update =
            page_name !== 'index' && normalized_page_path !== saved_page_path;
        const has_invalid_page_path_update =
            attempted_page_path_update && !normalized_page_path;
        const has_page_path_update =
            attempted_page_path_update && !has_invalid_page_path_update;
        const next_page_path = has_page_path_update
            ? normalized_page_path
            : saved_page_path;

        const nextPublished = parsePublishedValue(
            page_data?.[PAGE_PUBLISHED_KEY]
        );
        const prevPublished = parsePublishedValue(
            saved_page_data?.[PAGE_PUBLISHED_KEY]
        );
        const nextRedirectTarget = normalizeUnpublishRedirectTarget(
            page_data?.[PAGE_UNPUBLISH_REDIRECT_KEY]
        );
        const prevRedirectTarget = normalizeUnpublishRedirectTarget(
            saved_page_data?.[PAGE_UNPUBLISH_REDIRECT_KEY]
        );
        const shouldSyncUnpublishRedirect =
            has_page_path_update ||
            nextPublished !== prevPublished ||
            nextRedirectTarget !== prevRedirectTarget;
        const shouldSyncSitemapConfig = nextPublished !== prevPublished;

        if (has_invalid_page_path_update) {
            notifications.push({
                title: 'Invalid page URL',
                message: 'Page URL cannot be empty.',
                variant: 'error',
                ttl: 10,
            });
            setPagePath(saved_page_path);
        }

        const saved_manifest = {
            page_name,
            components: saved_components.slice(1, -1).map((component) => ({
                slug: component.data_file.replace('.json', ''),
                component_key: component.name,
            })),
        };

        const manifest = {
            page_name,
            components: components.slice(1, -1).map((component) => ({
                slug: component.data_file.replace('.json', ''),
                component_key: component.name,
            })),
        };

        if (JSON.stringify(manifest) !== JSON.stringify(saved_manifest)) {
            data.manifest = manifest;
        }

        const components_to_update = components.filter((component) => {
            const saved_component = saved_components.find(
                (sc) => sc.data_file === component.data_file
            );
            if (!saved_component) return true;
            return (
                JSON.stringify(component.props) !==
                JSON.stringify(saved_component.props)
            );
        });

        const components_to_delete = saved_components.filter(
            (sc) => !components.find((c) => c.data_file === sc.data_file)
        );

        if (JSON.stringify(page_data) !== JSON.stringify(saved_page_data)) {
            components_to_update.push({
                data_file: 'page.json',
                props: page_data,
            });
        }

        const themeGlobals = {
            ...(site_data?.fonts && Object.keys(site_data.fonts).length > 0
                ? { fonts: site_data.fonts }
                : {}),
            ...(site_data?.fontWeights &&
            Object.keys(site_data.fontWeights).length > 0
                ? { fontWeights: site_data.fontWeights }
                : {}),
        };
        const global_data_for_save = { ...global_data, ...themeGlobals };

        if (
            JSON.stringify(global_data_for_save) !==
            JSON.stringify(saved_global_data)
        ) {
            components_to_update.push({
                data_file: 'globals.json',
                props: global_data_for_save,
            });
        }

        data.components_to_update = components_to_update;
        data.components_to_delete = components_to_delete;

        if (
            !data.manifest &&
            !data.components_to_update.length &&
            !data.components_to_delete.length &&
            !has_page_path_update
        ) {
            console.log('No JSON changes to save!');
            return;
        }

        try {
            const payload = {
                page_name,
                data,
            };

            if (has_page_path_update) {
                payload.path = normalized_page_path;
            }

            await API.patch(`${global_data.company_id}/pages`, payload);

            setSavedComponents(components);
            setSavedPageData(page_data);
            setSavedPagePath(next_page_path);
            setPagePath(next_page_path);
            setGlobalData(global_data_for_save);
            setSavedGlobalData(global_data_for_save);

            if (shouldSyncUnpublishRedirect) {
                await syncUnpublishRedirectWithSiteSettings({
                    previous_page_path: saved_page_path,
                    next_page_path,
                    is_published: nextPublished,
                    redirect_target: nextRedirectTarget,
                });
            }

            if (shouldSyncSitemapConfig) {
                await syncSitemapConfigWithTemplate();
            }

            notifications.push({
                title: has_page_path_update
                    ? 'Page and URL saved successfully!'
                    : 'Page saved successfully!',
                message: has_page_path_update
                    ? `New URL: /${normalized_page_path}. Wait for the site to be rebuilt and refresh the page.`
                    : undefined,
                variant: 'success',
                ttl: 10,
            });
        } catch (error) {
            const saveErrorMessage =
                error?.payload?.message ||
                error?.payload?.error ||
                error?.message ||
                'Unknown error';
            notifications.push({
                title: 'Failed to save changes!',
                message: saveErrorMessage,
                variant: 'error',
                ttl: 20,
                error,
            });
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        await Promise.all([handleSaveToJSONFile(), handleSaveTheme()]);
        setIsSaving(false);
    };

    const handleRevertChanges = () => {
        setGlobalData(saved_global_data);
        setSiteData(saved_site_data);
        setPagePath(saved_page_path);
        setPageData(saved_page_data);
        setComponents(saved_components);
    };

    return (
        <EditorContext.Provider
            value={{
                global: {
                    data: global_data,
                    change: handleGlobalDataChange,
                    cancel: handleUndoGlobalDataChanges,
                },
                site: {
                    data: site_data,
                    change: handleSiteDataChange,
                    cancel: handleUndoSiteDataChanges,
                    addImage: handleAddImage,
                    deleteImage: handleDeleteImage,
                    regenerating_palette: isRegeneratingPalette,
                    open: handleOpenSiteSettings,
                    close: handleCloseSiteSettings,
                    visible: isSiteSettingsVisible,
                },
                page: {
                    name: page_name,
                    path: page_path,
                    data: page_data,
                    changePath: handlePagePathChange,
                    change: handlePageDataChange,
                    cancel: handleUndoPageDataChanges,
                    add: handleAddPage,
                    adding: adding_page,
                    delete: handleDeletePage,
                    deleting: deleting_page,
                    open: handleOpenPageSettings,
                    close: handleClosePageSettings,
                    visible: isPageSettingsVisible,
                },
                components: {
                    data: components,
                    change: handleComponentDataChange,
                    cancel: handleUndoComponentDataChanges,
                    generate: handleGenerateComponentData,
                    generating: isComponentGeneratingData,
                    add: handleAddComponent,
                    duplicate: handleDuplicateComponent,
                    move: handleMoveComponent,
                    delete: handleDeleteComponent,
                    open: handleOpenComponentEditor,
                    close: handleCloseComponentEditor,
                    visible: isComponentEditorVisible,
                    add_panel: {
                        open: handleOpenAddComponentPanel,
                        close: handleCloseAddComponentPanel,
                        visible: isAddComponentPanelVisible,
                        index: () => add_component_open_index,
                    },
                    nested: {
                        add: handleAddComponentNestedData,
                        delete: handleDeleteComponentNestedData,
                        move: handleMoveComponentNestedData,
                    },
                },
                is_saving,
                has_updates,
                save: handleSave,
                revert: handleRevertChanges,
            }}
        >
            {children}
        </EditorContext.Provider>
    );
}
